import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import type {
	AssignCommitmentMatrixBody,
	AssignCommitmentMatrixExemptBody,
	CommitmentExceptionListParams,
	CommitmentMatrixAssignmentParams,
	CommitmentMatrixDashboardParams,
	CommitmentMatrixEvolutionParams,
	CommitmentMatrixListParams,
	CommitmentMatrixSheetParams,
	CreateCommitmentExceptionBody,
	CreateCommitmentMatrixBody,
	MyCommitmentExceptionListParams,
	PersonTimelineParams,
	UpdateCommitmentExceptionStatusBody,
	UpdateCommitmentMatrixBody,
} from '@/types'
import {
	assignCommitmentMatrixToPerson,
	createCommitmentException,
	createCommitmentMatrix,
	deleteCommitmentException,
	deleteCommitmentMatrix,
	getCommitmentExceptions,
	getCommitmentMatrices,
	getCommitmentMatrixAssignmentPeople,
	getCommitmentMatrixDashboard,
	getCommitmentMatrixEvolution,
	getCommitmentMatrixTargetHistory,
	getMyCommitmentExceptions,
	getPersonCommitmentSheet,
	getPersonTimeline,
	setCommitmentMatrixExemptForPerson,
	updateCommitmentExceptionStatus,
	updateCommitmentMatrix,
} from '../services/commitment-matrix'

// ── Query keys ────────────────────────────────────────────────────────────────

export const commitmentMatrixKeys = {
	all: ['commitment-matrices'] as const,
	list: (params?: CommitmentMatrixListParams) =>
		[...commitmentMatrixKeys.all, 'list', params] as const,
	targetHistory: (id: string) =>
		[...commitmentMatrixKeys.all, 'target-history', id] as const,
	assignment: (params?: CommitmentMatrixAssignmentParams) =>
		[...commitmentMatrixKeys.all, 'assignment', params] as const,
	dashboard: (params?: CommitmentMatrixDashboardParams) =>
		[...commitmentMatrixKeys.all, 'dashboard', params] as const,
	evolution: (params?: CommitmentMatrixEvolutionParams) =>
		[...commitmentMatrixKeys.all, 'evolution', params] as const,
	sheet: (personId: string, params?: CommitmentMatrixSheetParams) =>
		[...commitmentMatrixKeys.all, 'sheet', personId, params] as const,
	timeline: (personId: string, params?: PersonTimelineParams) =>
		[...commitmentMatrixKeys.all, 'timeline', personId, params] as const,
}

export const commitmentExceptionKeys = {
	all: ['commitment-exceptions'] as const,
	list: (params?: CommitmentExceptionListParams) =>
		[...commitmentExceptionKeys.all, 'list', params] as const,
	me: (params?: MyCommitmentExceptionListParams) =>
		[...commitmentExceptionKeys.all, 'me', params] as const,
}

// ── GET /commitment-matrices ──────────────────────────────────────────────────

export function useCommitmentMatrices(params?: CommitmentMatrixListParams) {
	return useQuery({
		queryKey: commitmentMatrixKeys.list(params),
		queryFn: () => getCommitmentMatrices(params),
		staleTime: 2 * 60 * 1000,
		select: (data) => data.items,
	})
}

// ── GET /commitment-matrices/:id/target-history ──────────────────────────────

export function useCommitmentMatrixTargetHistory(id: string) {
	return useQuery({
		queryKey: commitmentMatrixKeys.targetHistory(id),
		queryFn: () => getCommitmentMatrixTargetHistory(id),
		enabled: !!id,
		select: (data) => data.items,
	})
}

// ── POST /commitment-matrices ─────────────────────────────────────────────────

export function useCreateCommitmentMatrix() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateCommitmentMatrixBody) =>
			createCommitmentMatrix(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── PATCH /commitment-matrices/:id ────────────────────────────────────────────

export function useUpdateCommitmentMatrix() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: UpdateCommitmentMatrixBody
		}) => updateCommitmentMatrix(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── DELETE /commitment-matrices/:id ───────────────────────────────────────────

export function useDeleteCommitmentMatrix() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteCommitmentMatrix(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── GET /commitment-matrices/people ───────────────────────────────────────────

export function useCommitmentMatrixAssignmentPeople(
	params?: CommitmentMatrixAssignmentParams,
) {
	return useQuery({
		queryKey: commitmentMatrixKeys.assignment(params),
		queryFn: () => getCommitmentMatrixAssignmentPeople(params),
		staleTime: 60 * 1000,
		placeholderData: keepPreviousData,
	})
}

// ── PATCH /person/:id/commitment-matrix ───────────────────────────────────────

export function useAssignCommitmentMatrixToPerson() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			personId,
			body,
		}: {
			personId: string
			body: AssignCommitmentMatrixBody
		}) => assignCommitmentMatrixToPerson(personId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── PATCH /person/:id/commitment-matrix-exempt ────────────────────────────────

export function useSetCommitmentMatrixExemptForPerson() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			personId,
			body,
		}: {
			personId: string
			body: AssignCommitmentMatrixExemptBody
		}) => setCommitmentMatrixExemptForPerson(personId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── GET /commitment-exceptions ────────────────────────────────────────────────

export function useCommitmentExceptions(
	params?: CommitmentExceptionListParams,
) {
	return useQuery({
		queryKey: commitmentExceptionKeys.list(params),
		queryFn: () => getCommitmentExceptions(params),
		staleTime: 60 * 1000,
	})
}

// ── GET /commitment-exceptions/me ─────────────────────────────────────────────

export function useMyCommitmentExceptions(
	params?: MyCommitmentExceptionListParams,
) {
	return useQuery({
		queryKey: commitmentExceptionKeys.me(params),
		queryFn: () => getMyCommitmentExceptions(params),
		staleTime: 60 * 1000,
	})
}

// ── POST /commitment-exceptions ───────────────────────────────────────────────

export function useCreateCommitmentException() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (body: CreateCommitmentExceptionBody) =>
			createCommitmentException(body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentExceptionKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── PATCH /commitment-exceptions/:id/status ───────────────────────────────────

export function useUpdateCommitmentExceptionStatus() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string
			body: UpdateCommitmentExceptionStatusBody
		}) => updateCommitmentExceptionStatus(id, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentExceptionKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── DELETE /commitment-exceptions/:id ─────────────────────────────────────────

export function useDeleteCommitmentException() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			activeBranchId,
		}: {
			id: string
			activeBranchId?: string
		}) => deleteCommitmentException(id, activeBranchId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: commitmentExceptionKeys.all })
			queryClient.invalidateQueries({ queryKey: commitmentMatrixKeys.all })
		},
	})
}

// ── GET /commitment-matrices/dashboard ────────────────────────────────────────

export function useCommitmentMatrixDashboard(
	params?: CommitmentMatrixDashboardParams,
) {
	return useQuery({
		queryKey: commitmentMatrixKeys.dashboard(params),
		queryFn: () => getCommitmentMatrixDashboard(params),
		staleTime: 60 * 1000,
		placeholderData: keepPreviousData,
	})
}

// ── GET /commitment-matrices/dashboard/evolution ──────────────────────────────

export function useCommitmentMatrixEvolution(
	params: CommitmentMatrixEvolutionParams,
	enabled: boolean,
) {
	return useQuery({
		queryKey: commitmentMatrixKeys.evolution(params),
		queryFn: () => getCommitmentMatrixEvolution(params),
		enabled,
		staleTime: 60 * 1000,
		placeholderData: keepPreviousData,
	})
}

// ── GET /person/:id/commitment-matrix-sheet ───────────────────────────────────

export function usePersonCommitmentSheet(
	personId: string | null,
	params?: CommitmentMatrixSheetParams,
) {
	return useQuery({
		queryKey: commitmentMatrixKeys.sheet(personId ?? '', params),
		queryFn: () => getPersonCommitmentSheet(personId as string, params),
		enabled: !!personId,
	})
}

// ── GET /commitment-matrices/dashboard/person/:id/timeline ───────────────────

export function usePersonTimeline(
	personId: string | null,
	params?: PersonTimelineParams,
) {
	return useQuery({
		queryKey: commitmentMatrixKeys.timeline(personId ?? '', params),
		queryFn: () => getPersonTimeline(personId as string, params),
		enabled: !!personId,
	})
}
